import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, AlertCircle, Loader2, Mic, MonitorSpeaker, ShieldCheck } from "lucide-react";

/**
 * PreFlightCheck — informs the closer that audio + permissions are in place
 * BEFORE the call starts, so nothing feels uncertain mid-conversation.
 *
 * Status values: "unknown" | "checking" | "ready" | "missing"
 *
 * For the prospect/tab audio, we cannot probe without a user gesture, so we
 * provide an explicit "Test Prospect Audio" button that briefly opens the
 * tab-picker, confirms an audio track is present, then releases the stream.
 */
const STATUS_LABEL = {
  unknown: "Not checked",
  checking: "Checking…",
  ready: "Ready",
  missing: "Not Ready",
};

const STATUS_TONE = {
  unknown: "bg-slate-100 text-slate-500 border-slate-200",
  checking: "bg-amber-50 text-amber-800 border-amber-200",
  ready: "bg-emerald-50 text-emerald-800 border-emerald-200",
  missing: "bg-orange-50 text-orange-800 border-orange-200",
};

function StatusPill({ status, testId }) {
  const Icon =
    status === "ready" ? CheckCircle2 : status === "checking" ? Loader2 : AlertCircle;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${STATUS_TONE[status]}`}
    >
      <Icon className={`h-3.5 w-3.5 ${status === "checking" ? "animate-spin" : ""}`} />
      {STATUS_LABEL[status]}
    </span>
  );
}

export default function PreFlightCheck({ onAllReady }) {
  const [mic, setMic] = useState("unknown");
  const [sys, setSys] = useState("unknown");

  // Try a passive permission probe for the mic on mount (no prompt if "granted").
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const p = await navigator.permissions.query({ name: "microphone" });
          if (cancelled) return;
          if (p.state === "granted") setMic("ready");
          else if (p.state === "denied") setMic("missing");
        }
      } catch (e) {
        /* permission API not available — leave as unknown */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const allReady = mic === "ready" && sys === "ready";
  const permissionsStatus = allReady ? "ready" : mic === "missing" || sys === "missing" ? "missing" : "unknown";

  useEffect(() => {
    onAllReady?.(allReady);
  }, [allReady, onAllReady]);

  const testMic = async () => {
    setMic("checking");
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      s.getTracks().forEach((t) => t.stop());
      setMic("ready");
    } catch (e) {
      setMic("missing");
    }
  };

  const testSys = async () => {
    setSys("checking");
    try {
      const s = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      const hasAudio = s.getAudioTracks().length > 0;
      s.getTracks().forEach((t) => t.stop());
      setSys(hasAudio ? "ready" : "missing");
    } catch (e) {
      setSys("missing");
    }
  };

  return (
    <motion.section
      data-testid="preflight-check"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-slate-500">
            Pre-Flight Check
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Confirm audio and permissions before you start the call.
          </p>
        </div>

        <AnimatePresence mode="wait">
          {allReady ? (
            <motion.span
              key="ready"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              data-testid="ready-for-call-badge"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-300 px-3 py-1.5 text-xs font-semibold text-emerald-800"
            >
              <CheckCircle2 className="h-4 w-4" />
              Ready for Call
            </motion.span>
          ) : (
            <motion.span
              key="notready"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              data-testid="not-ready-badge"
              className="inline-flex items-center gap-2 rounded-full bg-slate-100 border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600"
            >
              Setup needed
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      <ul className="divide-y divide-slate-100">
        <CheckRow
          icon={Mic}
          title="Closer Mic"
          desc="Captures the closer's voice."
          status={mic}
          actionLabel={mic === "ready" ? "Re-test" : "Test Mic"}
          onAction={testMic}
          testId="row-mic"
        />
        <CheckRow
          icon={MonitorSpeaker}
          title="Prospect Audio"
          desc='Pick the meeting tab and tick "Share tab audio".'
          status={sys}
          actionLabel={sys === "ready" ? "Re-test" : "Test Prospect Audio"}
          onAction={testSys}
          testId="row-sys"
        />
        <CheckRow
          icon={ShieldCheck}
          title="Permissions"
          desc="All required browser permissions granted."
          status={permissionsStatus}
          testId="row-permissions"
        />
      </ul>

      <p className="mt-4 text-[11px] leading-relaxed text-slate-500 border-t border-slate-100 pt-3">
        <strong className="text-slate-700">Speaker labels work best on remote calls.</strong>{" "}
        SCH labels by audio source: <span className="font-mono">mic = Closer</span>,{" "}
        <span className="font-mono">tab audio = Prospect</span>. In in-person mock calls,
        both voices enter the mic so everything is labeled "Closer" — that's expected, not a bug.
      </p>
    </motion.section>
  );
}

function CheckRow({ icon: Icon, title, desc, status, actionLabel, onAction, testId }) {
  return (
    <li className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0" data-testid={testId}>
      <div className="flex items-start gap-3 min-w-0">
        <Icon className="h-5 w-5 text-slate-400 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-900">{title}</div>
          <div className="text-xs text-slate-500 leading-snug">{desc}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <StatusPill status={status} testId={`${testId}-status`} />
        {onAction && (
          <button
            type="button"
            onClick={onAction}
            data-testid={`${testId}-action`}
            disabled={status === "checking"}
            className="text-xs rounded-md border border-slate-300 hover:border-slate-500 hover:bg-slate-50 px-2.5 py-1 text-slate-700 font-medium transition-colors disabled:opacity-50"
          >
            {actionLabel}
          </button>
        )}
      </div>
    </li>
  );
}
